// Opens calendar selection from the report's current date range.
// Users can click either the Start/End summary or Select range to edit those dates.
// The summary still shows the displayed report's dates while the calendar is being edited.
import { useState } from 'react';
import { PeriodOptionSet } from './createPeriodOptions.ts';
import { PeriodOption, Resolution } from '../report-settings/reportSettings.ts';
import { getPeriods } from './getPeriods.ts';
import { formatPeriodBoundary } from './periodRanges.ts';
import { DateRangeDialog } from './dateRangeDialog.tsx';

interface Props {
    first: PeriodOption;
    last: PeriodOption;
    periodOptionSet: PeriodOptionSet;
    resolution: Resolution;
    hideResolution?: boolean;
    onChange: (resolution: Resolution, first: PeriodOption, last: PeriodOption) => void;
}

export function ReportDateRangeControl({
    first,
    last,
    periodOptionSet,
    resolution,
    hideResolution = false,
    onChange,
}: Props) {
    const [isOpen, setIsOpen] = useState(false);
    const periods = getPeriods(periodOptionSet, resolution);
    const firstIndex = periods.indexOf(first);
    const lastIndex = periods.indexOf(last);
    const count = firstIndex === -1 || lastIndex === -1
        ? 0
        : lastIndex - firstIndex + 1;

    return (
        <div className='report-date-range-control'>
            <button
                type='button'
                className='report-date-range-summary'
                aria-label='Selected report date range'
                onClick={() => setIsOpen(true)}
            >
                <span className='report-date-range-summary__heading'>
                    <strong>Date range</strong>
                    <span>{count} {resolution.toLowerCase()}</span>
                </span>
                <RangeSummaryBoundary
                    label='Start'
                    value={formatPeriodBoundary(first, resolution, 'start')}
                />
                <RangeSummaryBoundary
                    label='End'
                    value={formatPeriodBoundary(last, resolution, 'end')}
                />
            </button>
            <button type='button' className='report-date-range-button' onClick={() => setIsOpen(true)}>
                Select range
            </button>

            {/* Unmount on close so reopening uses the applied range, not the discarded draft. */}
            {isOpen && (
                <DateRangeDialog
                    first={first}
                    last={last}
                    periodOptionSet={periodOptionSet}
                    initialResolution={resolution}
                    hideResolution={hideResolution}
                    onApply={(newResolution, newFirst, newLast) => {
                        onChange(newResolution, newFirst, newLast);
                        setIsOpen(false);
                    }}
                    onClose={() => setIsOpen(false)}
                />
            )}
        </div>
    );
}

function RangeSummaryBoundary({ label, value }: { label: string; value: string }) {
    return (
        <span className='report-date-range-summary__boundary'>
            <span>{label}</span>
            <strong>{value}</strong>
        </span>
    );
}
